import { Controller } from "stimulus"
import { calcSmoothedOD, calcGrowSpeed, calcRelativeGrowSpeed } from '../utils/growSpeed'
import Highcharts from 'highcharts'
import HighchartsMore from 'highcharts/highcharts-more'
import HighchartsBoost from 'highcharts/modules/boost'

HighchartsMore(Highcharts)
HighchartsBoost(Highcharts)

const getKey = (chartName, seriesName) => `${chartName}/${seriesName}`;

function restoreSeriesVisibility(chartName, chartSeries) {
  chartSeries.forEach(series => {
    if (!series.name) return

    const seriesVisible = localStorage.getItem(getKey(chartName, series.name))

    if (seriesVisible === null) {
      return
    } else if (seriesVisible === 'true') {
      series.show()
    } else {
      series.hide()
    }
  })
}

function saveSeriesVisibility(chartName, seriesName, visible) {
  if (!seriesName) return

  localStorage.setItem(getKey(chartName, seriesName), String(visible))
}

const formatDateWithTimezone = (timestamp, timezone) => {
  const date = new Date(timestamp)
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short'
  }

  if (timezone) {
    options.timeZone = timezone
  }

  return new Intl.DateTimeFormat('ru-RU', options).format(date)
}

const createTooltipFormatter = (timezone) => function () {
  const point = this.points[0].point

  return `
    <div>${formatDateWithTimezone(point.options.x, timezone)}</div>
    ${this.points
    .map(
      p =>
        `<span style="color:${p.series.color}">\u25CF</span> ${p.series.name}: <b>${p.point.options.custom?.tooltipValue ?? p.y}</b><br/>`
    )
    .join('')}
  `
}

export default class extends Controller {
  static targets = [
    'metricTypes',
    'chart',
    'acidityLast',
    'acidityMin',
    'acidityMax',
    'temperatureLast',
    'temperatureMin',
    'temperatureMax',
    'opticalDensityLast',
    'opticalDensityMin',
    'opticalDensityMax',
    'concentrationLast',
    'concentrationMin',
    'concentrationMax',
    'dissolvedOxygenLast',
    'dissolvedOxygenMin',
    'dissolvedOxygenMax',
    'flowRateLast',
    'flowRateMin',
    'flowRateMax',
  ]

  static values = {
    acidityUrl: String,
    opticalDensityUrl: String,
    oxygenUrl: String,
    temperatureUrl: String,
    detailedMetrics: Boolean,
    timezone: String,
  }

  connect() {
    if (this.initialized) return
    this.initialized = true

    this.metricTypes = JSON.parse(this.metricTypesTarget.textContent)

    setTimeout(() => {
      this.showComments = true
      this.initChart()
      this.reloadData()
    }, 100)
  }

  toggleDetailedMetrics() {
    this.detailedMetricsValue = !this.detailedMetricsValue
  }

  detailedMetricsValueChanged() {
    console.log("detailedMetricsValueChanged")
    if (this.chartTarget.highchart) {
      this.reloadData()
    }
  }

  reloadData() {
    this.highchart.showLoading()
    this.fetchRecords()
      .then(data => this.setData(data))
      .then(() => this.highchart.hideLoading())
  }

  initChart() {
    this.chartTarget.highchart = new Highcharts.chart(this.chartTarget, this.chartOptions)
  }

  fetchRecords() {
    const recordsUrl = this.element.getAttribute('data-records-url')
    return fetch(recordsUrl, { headers: { Accept: 'application/json' } }).then(response => response.json())
  }

  setData(data) {
    this.records = data.map(record => ({ ...record, recorded_at: Date.parse(record.recorded_at) }))
    this.setOpticalDensityData(data)

    const predefinedMetrics = new Set(['optical_density', 'flow_rate'])
    const additionalMetrics = this.metricTypes.filter(el => !predefinedMetrics.has(el.code))
    additionalMetrics.forEach(metric => {
      const series = this.highchart.series.find(s => s.options.code === metric.code)
      if (series) {
        series.update({
          data: this.records.map(el => [el.recorded_at, el[metric.code]]),
          type: "spline",
        })
      } else {
        this.highchart.addAxis({ title: metric.name, visible: false }, false, false)
        const yAxis = this.highchart.yAxis.length - 1
        this.highchart.addSeries({
          name: metric.name,
          yAxis,
          data: this.records.map(el => [el.recorded_at, el[metric.code]]),
          type: "spline",
          visible: false,
        })
      }
    })

    this.setIndicators(this.records)
  }

  setOpticalDensityData(data) {
    const smoothedOD = calcSmoothedOD(data)
    const smoothedGrowSpeed = calcGrowSpeed(data)
    const relativeGrowSpeed = calcRelativeGrowSpeed(data)
    const flow_rate = data.filter(el => el.optical_density != null && el.flow_rate != null).map(el => {
      const flowRate = Math.min(1, Number(el.flow_rate))
      const opticalDensity = Number(el.optical_density)
      const gap = opticalDensity * (1 - flowRate) * 0.5

      return {
        x: Date.parse(el.recorded_at),
        low: opticalDensity + gap,
        high: opticalDensity - gap,
        custom: {
          tooltipValue: flowRate
        }
      }
    })

    const points = data.map(record => ({
      x: Date.parse(record.recorded_at),
      y: Number(record.optical_density),
      concentration: Number(record.concentration),
      acidity: Number(record.acidity),
      temperature: Number(record.temperature),
      flow_rate: Number(record.flow_rate),
      oxygen: Number(record.oxygen),
    }))

    this.highchart.series[2].setData(points)

    this.highchart.series[4].setData(smoothedOD.map(el => [
      Date.parse(el.recorded_at),
      el.smoothedOD,
    ]))
    this.highchart.series[5].setData(smoothedGrowSpeed.map(el => [
      Date.parse(el.recorded_at),
      el.smoothedGrowSpeed,
    ]))
    this.highchart.series[6].setData(relativeGrowSpeed.map(el => [
      Date.parse(el.recorded_at),
      el.relativeGrowSpeed,
    ]))

    this.highchart.series[3].setData(flow_rate)
  }

  setIndicators(data) {
    const acidity = data.filter(el => el.acidity != null).map(el => el.acidity)
    const temperature = data.filter(el => el.temperature != null).map(el => el.temperature)
    const opticalDensity = data.filter(el => el.optical_density != null).map(el => el.optical_density)
    const concentration = data.filter(el => el.optical_density != null).map(el => el.optical_density * 0.2)
    const dissolvedOxygen = data.filter(el => el.dissolved_oxygen != null).map(el => el.dissolved_oxygen)
    const flowRate = data.filter(el => el.flow_rate != null).map(el => el.flow_rate)

    this.acidityLast = this.average(acidity)
    const acidityStats = this.calculateMinMax(acidity);
    this.acidityMin = acidityStats.min;
    this.acidityMax = acidityStats.max;

    this.temperatureLast = this.average(temperature)
    const temperatureStats = this.calculateMinMax(temperature);
    this.temperatureMin = temperatureStats.min;
    this.temperatureMax = temperatureStats.max;

    this.opticalDensityLast = this.average(opticalDensity)
    const opticalDensityStats = this.calculateMinMax(opticalDensity);
    this.opticalDensityMin = opticalDensityStats.min
    this.opticalDensityMax = opticalDensityStats.max

    this.concentrationLast = this.average(concentration)
    const concentrationStats = this.calculateMinMax(concentration);
    this.concentrationMin = concentrationStats.min
    this.concentrationMax = concentrationStats.max

    this.dissolvedOxygenLast = this.average(dissolvedOxygen)
    const dissolvedOxygenStats = this.calculateMinMax(dissolvedOxygen);
    this.dissolvedOxygenMin = dissolvedOxygenStats.min
    this.dissolvedOxygenMax = dissolvedOxygenStats.max;

    this.flowRateLast = this.average(flowRate)
    const flowRateStats = this.calculateMinMax(flowRate);
    this.flowRateMin = flowRateStats.min
    this.flowRateMax = flowRateStats.max;
  }

  average(nums) {
    if (nums.length > 0) {
      return nums.reduce((a, b) => (a + b)) / nums.length;
    } else {
      return null
    }
  }

  calculateMinMax(data) {
    if (data.length === 0) {
      return { min: null, max: null };
    }
    return {
      min: Math.min(...data),
      max: Math.max(...data),
    };
  }

  setSowings(data) {
    data.forEach(sowing => {
      this.highchart.xAxis[0].addPlotLine({
        value: Date.parse(sowing.sowed_at),
        color: '#0B3954',
        width: 1,
        label: {
          text: sowing.name,
          rotation: 270,
          x: 15,
          verticalAlign: 'middle',
          textAlign: 'center',
          style: {
            color: '#5E6472',
            fontWeight: 'bold'
          }
        }
      })
    })
  }

  formatNumber(value, digits = 1) {
    return (Math.round(value * Math.pow(10, digits)) / Math.pow(10, digits)).toFixed(digits)
  }

  set acidityLast(value) {
    this.acidityLastTarget.innerText = value != null ? this.formatNumber(value) : "--.-"
  }

  set acidityMin(value) {
    this.acidityMinTarget.innerText = value != null ? this.formatNumber(value) : "--.-"
  }

  set acidityMax(value) {
    this.acidityMaxTarget.innerText = value != null ? this.formatNumber(value) : "--.-"
  }

  set temperatureLast(value) {
    this.temperatureLastTarget.innerText = value != null ? this.formatNumber(value) : "--.-"
  }

  set temperatureMin(value) {
    this.temperatureMinTarget.innerText = value != null ? this.formatNumber(value) : "--.-"
  }

  set temperatureMax(value) {
    this.temperatureMaxTarget.innerText = value != null ? this.formatNumber(value) : "--.-"
  }

  set opticalDensityLast(value) {
    this.opticalDensityLastTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set opticalDensityMin(value) {
    this.opticalDensityMinTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set opticalDensityMax(value) {
    this.opticalDensityMaxTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set concentrationLast(value) {
    this.concentrationLastTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set concentrationMin(value) {
    this.concentrationMinTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set concentrationMax(value) {
    this.concentrationMaxTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set dissolvedOxygenLast(value) {
    this.dissolvedOxygenLastTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set dissolvedOxygenMin(value) {
    this.dissolvedOxygenMinTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set dissolvedOxygenMax(value) {
    this.dissolvedOxygenMaxTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set flowRateLast(value) {
    this.flowRateLastTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set flowRateMin(value) {
    this.flowRateMinTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  set flowRateMax(value) {
    this.flowRateMaxTarget.innerText = value != null ? this.formatNumber(value, 2) : "--.-"
  }

  get highchart() {
    return this.chartTarget.highchart
  }

  triggerComments(event) {
    const element = event.target
    this.showComments = !this.showComments
    element.innerText = this.showComments ? 'Hide comments' : 'Show comments'
    const displayValue = this.showComments ? 'display' : 'none'
    this.highchart.xAxis[0].plotLinesAndBands.forEach(el => {
      if (el.options.className === 'comment') {
        el.options.width = this.showComments ? 1 : 0
      }
    })

    this.highchart.update({
      plotOptions: {
        series: {
          dataLabels: {
            style: {
              display: displayValue,
            }
          }
        }
      }
    })

    this.highchart.xAxis[0].update()
  }

  setExtremes(event) {
    const data = this.records.filter(el => el.recorded_at > event.min && el.recorded_at < event.max)
    this.setIndicators(data)
  }

  get chartOptions() {
    const showHideAxisTitle = {
      hide: function () {
        this.yAxis.axisTitle.attr({ text: null })
      },
      show: function () {
        this.yAxis.axisTitle.attr({ text: this.yAxis.userOptions.title.text })
      }
    }

    const pointFormatter = function (a, b, c) {
      // console.log(this)
      const selectedX = this.x
      let str = ""

      if (this.series.name == "Optical density") {
        str += `
          pH: <b>${this.acidity.toFixed(2)}</b><br>
          Temperature: <b>${this.temperature.toFixed(2)}</b><br>
          ${this.series.name}: <b>${this.y.toFixed(2)}</b><br>
          Flow rate: <b>${this.flow_rate.toFixed(2)}</b><br>
          Oxygen: <b>${this.oxygen.toFixed(2)}</b><br>
        `
      } else {
        str += `
          ${this.series.name}: <b>${this.y.toFixed(2)}</b><br>
        `
      }

      return str

      this.series.chart.series.forEach(s => {
        //return // temp

        if (s.name == "Flow rate") {
          return
        }

        // console.log(s.points)
        let p = s.points.find(p => {
          return p.x == selectedX
        })
        if (p) {
          console.log(p)
          str += `
            ${s.name}: <b>${p.y.toFixed(2)}</b><br>
          `

          if (s.name == "Optical density") {
            //str += `
            //  Flow rate: <b>${p.flowRate.toFixed(2)}</b><br>
            //`
          }
        }
      })
      return str
      // return `
      //   ${this.series.name}: <b>${this.y}</b><br>
      //
      //   <span style="color:${this.series.color}">●</span> ${this.series.name}: <b>${this.y}</b><br>
      //   <span style="color:${this.series.color}">-</span> Temperature: <b>${this.options.temperature}</b><br>
      //   <span style="color:${this.series.color}">-</span> Flow rate: <b>${this.options.flowRate}</b><br>
      //   <span style="color:${this.series.color}">-</span> Mixer speed: <b>${this.options.mixerSpeed}</b><br>
      // `
    }

    const chartName = 'stand_records';

    return {
      chart: {
        zoomType: 'x',
        resetZoomButton: {
          position: {
            align: 'left',
            x: 10,
          }
        },
        events: {
          load: function () {
            restoreSeriesVisibility(chartName, this.series)
          }
        }
      },
      title: {
        text: null
      },
      tooltip: {
        enabled: true,
        shared: true,
        useHTML: true,
        formatter: createTooltipFormatter(this.timezoneValue || null)
      },
      credits: {
        enabled: false
      },
      plotOptions: {
        spline: {
          marker: {
            enabled: false,
            symbol: "circle",
          },
        },
        line: {
          marker: {
            symbol: "circle",
          }
        },
        series: {
          turboThreshold: 2000,
          // marker: {
          //   radius: 1
          // },
          dataLabels: {
            shape: 'callout',
            backgroundColor: 'rgba(0, 0, 0, 0.57)',
            style: {
              color: '#FFFFFF',
              textShadow: 'none',
              width: '200px'
            }
          },
          events: {
            legendItemClick: function () {
              saveSeriesVisibility(chartName, this.options.name, !this.visible)
            }
          },
        }
      },
      xAxis: {
        type: 'datetime',
        crosshair: true,
        events: {
          setExtremes: this.setExtremes.bind(this)
        }
      },
      yAxis: [
        { // 0
          title: {
            text: 'pH',
            style: {
              color: '#cccccc' //'#7cb5ec'
            }
          },
          labels: {
            style: {
              color: '#cccccc' //'#7cb5ec'
            }
          }
        },
        { // 1
          title: {
            text: 'Temperature',
            style: {
              color: '#434348'
            }
          },
          labels: {
            style: {
              color: '#434348'
            }
          }
        },
        { // 2
          title: {
            text: 'Optical density',
            style: {
              color: '#90ed7d'
            }
          },
          labels: {
            style: {
              color: '#90ed7d'
            }
          },
          opposite: true,
          min: 0,
        },
        { // 4
          title: {
            text: 'Growth rate',
            style: {
              color: '#8C4843'
            }
          },
          labels: {
            style: {
              color: '#8C4843'
            }
          },
          visible: false,
          opposite: true
        },
        { // 5
          title: {
            text: 'Rel. growth rate',
            style: {
              color: '#8C4843'
            }
          },
          labels: {
            style: {
              color: '#8C4843'
            }
          },
          visible: false,
          opposite: true
        },
        { // 6
          title: {
            text: 'Oxygen',
            style: {
              color: '#7cb5ec' // '#CC00CC'
            }
          },
          labels: {
            style: {
              color: '#7cb5ec' // '#CC00CC'
            }
          },
          visible: true,
          opposite: true,
        },
      ],
      series: [
        { // 0
          name: 'pH',
          code: 'acidity',
          type: 'line',
          boostThreshold: 1,
          color: '#cccccc', // '#7cb5ec',
          yAxis: 0,
          events: showHideAxisTitle,
          visible: false,
        },
        { // 1
          name: 'Temperature',
          code: 'temperature',
          type: 'line',
          color: '#434348',
          yAxis: 1,
          events: showHideAxisTitle,
          visible: false,
          boostThreshold: 1,
        },
        { // 2
          name: 'Optical density',
          code: 'optical_density',
          type: 'spline',
          marker: {
            enabled: true,
            radius: 3,
            symbol: "cirle",
          },
          color: '#90ed7d',
          yAxis: 2,
          events: showHideAxisTitle,
        },
        { // 3
          name: 'Flow rate',
          code: 'flow_rate',
          type: 'arearange',
          lineWidth: 0,
          linkedTo: ':previous',
          color: '#90ed7d',
          yAxis: 2,
          fillOpacity: 0.25,
          zIndex: 0,
          marker: {
            enabled: false
          },
          // enableMouseTracking: false,
          visible: true,
        },
        { // 5
          name: 'Smoothed OD',
          code: 'smoothed_optical_density',
          type: 'spline',
          color: '#407076',
          yAxis: 2,
          events: showHideAxisTitle,
          visible: false,
        },
        { // 6
          name: 'OD change rate smoothed',
          code: 'smoothed_grow_speed',
          type: 'spline',
          color: '#8C4843',
          yAxis: 3,
          visible: false,
        },
        { // 7
          name: 'Rel. OD change rate',
          code: 'relative_grow_speed',
          type: 'spline',
          color: '#FE938C',
          yAxis: 4,
          visible: false,
        },
        { // 8
          name: 'Dissolved Oxygen',
          code: 'dissolved_oxygen',
          type: 'line',
          color: '#7cb5ec', // '#CC00CC',
          yAxis: 5,
          events: showHideAxisTitle,
          boostThreshold: 1,
          visible: true,
        },
      ]
    }
  }
}
